import { Button, Flex, Image, Link } from '@chakra-ui/react'
import { useLocation, useNavigate } from 'react-router-dom'

import { logout } from '@/api/auth'
import { H5 } from '@/components/ui/Headings'
import { useBrandContext } from '@/context/brand-context'
import { useLogoBgColor } from '@/hooks/use-logo-bg-color'

const NAV_ITEMS = [
  { label: 'Gallery', path: '/gallery' },
  { label: 'Library', path: '/library' }
]

export default function AppNavbar() {
  const { brandAnalysis } = useBrandContext()
  const navigate = useNavigate()
  const location = useLocation()
  const brandName = brandAnalysis?.brand.name ?? ''
  const brandLogo = brandAnalysis?.brand.logo ?? null
  const brandColor =
    brandAnalysis?.brand.colors?.[0]?.hex ?? null
  const logoBgColor = useLogoBgColor(brandLogo)

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/')

  return (
    <Flex
      as="nav"
      position="sticky"
      top={0}
      zIndex={100}
      h="64px"
      px={{ base: '16px', md: '32px' }}
      alignItems="center"
      justifyContent="space-between"
      bg="rgba(10, 10, 15, 0.8)"
      backdropFilter="blur(20px) saturate(180%)"
      borderBottom="1px solid"
      borderColor="border.subtle"
      className="animate-fade-in"
    >
      <Flex alignItems="center" gap="12px">
        {brandLogo && (
          <Image
            src={brandLogo}
            alt={brandName}
            w="28px"
            h="28px"
            borderRadius="6px"
            objectFit="contain"
            bg={logoBgColor}
            p={logoBgColor ? '2px' : undefined}
          />
        )}
        <H5 color="text.primary" fontWeight={300}>
          {brandName}
        </H5>
      </Flex>

      <Flex alignItems="center" gap="4px">
        {NAV_ITEMS.map((item) => (
          <Button
            key={item.path}
            h="36px"
            px="16px"
            textStyle="bodySmall"
            fontWeight={isActive(item.path) ? 500 : 400}
            borderRadius="9999px"
            bg={isActive(item.path) ? 'bg.tertiary' : 'transparent'}
            color={isActive(item.path) ? 'text.primary' : 'text.tertiary'}
            transition="all 0.3s ease"
            _hover={{
              color: 'text.primary',
              bg: 'bg.tertiary'
            }}
            onClick={() => navigate(item.path)}
          >
            {item.label}
          </Button>
        ))}
      </Flex>

      <Flex alignItems="center" gap="16px">
        <Link
          href="mailto:michael@spacerunners.com"
          display="inline-flex"
          alignItems="center"
          h="36px"
          px="20px"
          textStyle="bodySmall"
          fontWeight={500}
          borderRadius="9999px"
          bg={brandColor ?? 'accent.500'}
          color="white"
          transition="all 0.3s ease"
          _hover={{
            opacity: 0.85,
            transform: 'translateY(-1px)',
            boxShadow: brandColor
              ? `0 0 30px ${brandColor}4D`
              : '0 0 30px rgba(0, 112, 243, 0.3)',
            textDecoration: 'none'
          }}
        >
          Chat with Us
        </Link>
        <Button
          h="36px"
          px="16px"
          textStyle="bodySmall"
          fontWeight={400}
          borderRadius="9999px"
          bg="transparent"
          color="text.tertiary"
          border="1px solid"
          borderColor="border.subtle"
          transition="all 0.3s ease"
          _hover={{
            color: 'text.primary',
            borderColor: 'border.strong'
          }}
          onClick={logout}
        >
          Sign Out
        </Button>
      </Flex>
    </Flex>
  )
}
